import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, EMPTY, filter, from, map, mergeMap, of, switchMap, take, tap, withLatestFrom} from 'rxjs';
import * as LocationActions from './location.actions';
import {
  assignLocation,
  confirmUnassignLocation,
  createLocationSuccess,
  unassignLocation,
  unassignLocationError,
  unassignLocationSuccess
} from './location.actions';
import {LocationApiService} from '../services/location-api.service';
import {Store} from '@ngrx/store';
import {Router} from '@angular/router';
import {ConfirmPopupComponent} from '@components/confirm-popup/confirm-popup.component';
import {
  deleteCollectionItem,
  deleteCollectionItemAborted,
  loadCollectionItem, loadCollectionItemError,
} from '../../collection-item/store/collection-item.actions';
import {MatDialog} from '@angular/material/dialog';
import {TranslocoService} from '@jsverse/transloco';
import {MatSnackBar} from '@angular/material/snack-bar';
import {GlobalState} from '../../../store/store.model';
import {ErrorDialogComponent} from '../../../shared/components/error-dialog/error-dialog.component';
import {SuccessDialogComponent} from '../../../shared/components/success-dialog/success-dialog.component';
import {collectionItemId} from '../../collection-item/store/collection-item.selectors';
import {selectGuest} from '../../flow/store/flow.selectors';

@Injectable()
export class LocationEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _locationApiService = inject(LocationApiService);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _snackbar = inject(MatSnackBar);


  public _loadLocations = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.loadLocations),
      withLatestFrom(this._store.select(selectGuest)),
      switchMap(([{}, guestMode]) => {
        if (!guestMode) {
          return this._locationApiService.getLocations().pipe(
            map(response =>
              LocationActions.loadLocationsSuccess({
                locations: response.locations,
                status: 'success',
              })
            ),
            catchError(() => of(LocationActions.loadLocationsError()))
          )
        } else {
          return EMPTY;
        }
      })
    )
  );

  private _loadLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.loadLocation),
      switchMap(({locationId}) =>
        this._locationApiService.getLocation(locationId).pipe(
          map(response =>
            LocationActions.loadLocationSuccess({location: response.location})
          ),
          catchError(() => of(LocationActions.loadLocationError()))
        )
      )
    )
  );

  private _createLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.createLocation),
      switchMap(({location, collectionItemId}) =>
        this._locationApiService.createLocation(location).pipe(
          map(response =>
            LocationActions.createLocationSuccess({
              location: response.result?.location,
              collectionItemId
            })
          ),
          catchError(() => of(LocationActions.createLocationError()))
        )
      )
    )
  );

  private _createLocationSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(createLocationSuccess),
      tap(({location, collectionItemId, collectionItemIds}) => {
        if (location && collectionItemId) {
          this._store.dispatch(assignLocation({
            locationId: location.id,
            collectionItemId,
            assigningToIds: collectionItemIds
          }));
        } else {
          this._router.navigate(['/tabs/location']);
        }
      })
    );
  }, {dispatch: false});

  private _editLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.editLocation),
      switchMap(({location}) =>
        this._locationApiService.editLocation(location).pipe(
          map(response =>
            LocationActions.editLocationSuccess({location: response.location})
          ),
          catchError(() => of(LocationActions.editLocationError()))
        )
      )
    )
  );

  private _deleteLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.deleteLocation),
      switchMap(({locationId}) =>
        this._locationApiService.deleteLocation(locationId).pipe(
          mergeMap(response => [
              LocationActions.deleteLocationSuccess({status: response.message}),
              LocationActions.loadLocations()
            ]
          ),
          catchError(() => of(LocationActions.deleteLocationError()))
        )
      )
    )
  );

  private _assignLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(LocationActions.assignLocation),
      switchMap(({locationId, collectionItemId, quantity, assigningToIds}) =>
        this._locationApiService.assignLocation(Number(locationId), Number(collectionItemId), quantity ?? 1, assigningToIds || []).pipe(
          take(1),
          map(response => {
              return LocationActions.assignLocationSuccess({
                status: response.result.status,
                collectionItemId: collectionItemId
              })
            }
          ),
          catchError(() => of(LocationActions.assignLocationError({error: this._translocoService.translate('location.server_assign_error')})))
        )
      )
    )
  );

  private _assignLocationSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(LocationActions.assignLocationSuccess),
      switchMap(({collectionItemId}) => {
        const success = this._dialog.open(SuccessDialogComponent, {
          data: {
            message: this._translocoService.translate('snackbar.assign_location_success'),
          },
        });

        return success.afterClosed().pipe(
          tap(() => {
            this._router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
              this._router.navigate([`/tabs/collection-item/${collectionItemId}`], {state: {locationTabType: 'position'}});
            });
          }),
          catchError(() => of(loadCollectionItemError()))
        );
      })
    );
  }, {dispatch: false});


  private _assignLocationError = createEffect(() => {
    return this._actions$.pipe(
      ofType(LocationActions.assignLocationError),
      tap(({error}) => {
        this._dialog.open(ErrorDialogComponent, {
          data: {message: error}
        })
      })
    );
  }, {dispatch: false});

  private _confirmUnassignLocation = createEffect(() => {
    return this._actions$.pipe(
      ofType(confirmUnassignLocation),
      switchMap(({collectionItemId}) => {
        const dialogRef = this._dialog.open(ConfirmPopupComponent, {
          data: {collectionItemId: collectionItemId},
          width: '30rem',
          disableClose: true
        });

        return dialogRef.afterClosed().pipe(
          take(1),
          filter(confirmed => confirmed === true),
          map(() => unassignLocation({collectionItemId}))
        );
      })
    );
  });

  private _unassignLocation = createEffect(() =>
    this._actions$.pipe(
      ofType(unassignLocation),
      switchMap(({collectionItemId}) =>
        this._locationApiService.unassignLocation(collectionItemId).pipe(
          take(1),
          switchMap((res) => {
            const status = res.result?.status;
            if (status === 'success') {
              return from([
                unassignLocationSuccess(),
                loadCollectionItem({collectionItemId})
              ]);
            } else {
              return of(unassignLocationError());
            }
          }),
          catchError(() => of(unassignLocationError()))
        )
      )
    )
  );

  private _unassignLocationSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(unassignLocationSuccess),
      tap(() => {
        this._translocoService.selectTranslate('snackbar.unassign_location_success').pipe(
          take(1)
        ).subscribe(msg => {
          this._snackbar.open(
            msg,
            'Ok',
            {
              duration: 3000,
              panelClass: 'alert-snackbar'
            });
        })
      })
    )
  }, {dispatch: false});

  private _resetLoadingStatus = createEffect(() =>
    this._actions$.pipe(
      ofType(
        LocationActions.loadLocationsSuccess,
        LocationActions.loadLocationsError,
        LocationActions.createLocationSuccess,
        LocationActions.createLocationError,
        LocationActions.editLocationSuccess,
        LocationActions.editLocationError,
        LocationActions.deleteLocationSuccess,
        LocationActions.deleteLocationError
      ),
      map(() => LocationActions.resetLoadingStatus())
    )
  );
}
